import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { TrackMap } from "./TrackMap";
import { Button } from "./ui/button";
import { Navigation, MapPin, Clock, Radio, Square, PlayCircle, Loader2 } from "lucide-react";

export function TrackingPanel({ errandId, isRunner }) {
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [demo, setDemo] = useState(false);
  const watchId = useRef(null);
  const demoTimer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/errands/${errandId}/location`);
      setData(data);
      return data;
    } catch (e) { return null; }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => {
      clearInterval(t);
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
      if (demoTimer.current) clearInterval(demoTimer.current);
    };
    // eslint-disable-next-line
  }, [errandId]);

  const post = async (lat, lng) => {
    try {
      await api.post(`/errands/${errandId}/location`, { lat, lng });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startSharing = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported on this device");
    setSharing(true);
    stopDemo();
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => post(pos.coords.latitude, pos.coords.longitude),
      () => { toast.error("Couldn't access GPS. Try demo mode instead."); setSharing(false); },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
    toast.success("Sharing your live location");
  };

  const stopSharing = () => {
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    setSharing(false);
  };

  const startDemo = async () => {
    const d = data || (await load());
    if (!d?.dest_lat) return toast.error("No destination coordinates for this errand yet");
    stopSharing();
    setDemo(true);
    // start ~2km southwest of destination and walk toward it
    let lat = d.dest_lat - 0.018;
    let lng = d.dest_lng - 0.018;
    const steps = 14;
    let i = 0;
    await post(lat, lng);
    demoTimer.current = setInterval(async () => {
      i += 1;
      lat = lat + (d.dest_lat - lat) * 0.28;
      lng = lng + (d.dest_lng - lng) * 0.28;
      await post(lat, lng);
      if (i >= steps) stopDemo();
    }, 3000);
    toast.success("Demo route started");
  };

  const stopDemo = () => {
    if (demoTimer.current) { clearInterval(demoTimer.current); demoTimer.current = null; }
    setDemo(false);
  };

  if (!data) return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 flex justify-center text-[#6B7280]">
      <Loader2 className="animate-spin" />
    </div>
  );

  const runner = data.runner_lat != null ? { lat: data.runner_lat, lng: data.runner_lng } : null;
  const dest = data.dest_lat != null ? { lat: data.dest_lat, lng: data.dest_lng } : null;

  return (
    <div data-testid="tracking-panel" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-extrabold text-lg text-[#1A1D20] flex items-center gap-2">
          <Navigation size={18} className="text-primary" /> Live tracking
        </h2>
        {runner && (
          <span className="flex items-center gap-1.5 text-xs font-bold text-[#2F8F6E]">
            <span className="w-2 h-2 rounded-full bg-[#66C2A5] animate-pulse" /> Live
          </span>
        )}
      </div>

      {!dest ? (
        <p className="text-sm text-[#9CA3AF] text-center py-8">We couldn't map this errand's location. Tracking is unavailable.</p>
      ) : (
        <>
          <div data-testid="track-map" className="overflow-hidden rounded-3xl border border-gray-100 mb-4">
            <TrackMap runner={runner} dest={dest} />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-muted rounded-2xl p-3">
              <p className="text-[10px] font-bold uppercase text-[#9CA3AF] flex items-center gap-1"><MapPin size={11} /> Distance</p>
              <p data-testid="track-distance" className="font-heading font-black text-xl text-[#1A1D20]">
                {data.distance_km != null ? `${data.distance_km} km` : "—"}
              </p>
            </div>
            <div className="bg-muted rounded-2xl p-3">
              <p className="text-[10px] font-bold uppercase text-[#9CA3AF] flex items-center gap-1"><Clock size={11} /> ETA</p>
              <p data-testid="track-eta" className="font-heading font-black text-xl text-primary">
                {data.eta_min != null ? `${data.eta_min} min` : "—"}
              </p>
            </div>
          </div>

          {isRunner ? (
            <div className="flex flex-wrap gap-2">
              {!sharing ? (
                <Button data-testid="share-location-btn" onClick={startSharing}
                  className="flex-1 rounded-full h-11 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
                  <Radio size={16} className="mr-2" /> Share live location
                </Button>
              ) : (
                <Button data-testid="stop-location-btn" onClick={stopSharing} variant="outline"
                  className="flex-1 rounded-full h-11 font-bold border-2">
                  <Square size={15} className="mr-2" /> Stop sharing
                </Button>
              )}
              {!demo ? (
                <Button data-testid="demo-route-btn" onClick={startDemo} variant="outline"
                  className="rounded-full h-11 font-semibold border-2 text-[#4A5361]">
                  <PlayCircle size={16} className="mr-2" /> Demo route
                </Button>
              ) : (
                <Button data-testid="stop-demo-btn" onClick={stopDemo} variant="ghost"
                  className="rounded-full h-11 font-semibold text-[#B23B3B]">
                  <Square size={15} className="mr-2" /> Stop demo
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#6B7280] text-center">
              {runner ? `${data.runner_name || "Your runner"} is on the way.` : "Waiting for your runner to start sharing their location…"}
            </p>
          )}
        </>
      )}
    </div>
  );
}
